import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import { ArrowRight, Sparkles, Users, Award, Zap, ChevronRight, Star } from 'lucide-react';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

import { BRAND, STATS, PRODUCTS, TESTIMONIALS, CLIENT_LOGOS } from '../data/mock';

import imirrorImg from '../assets/images/imirror.jpeg';
import airboothImg from '../assets/images/airbooth.jpg';
import retroBoothImg from '../assets/images/retro_booth.png';

// Photo booth related hero images
const heroSlides = [
  {
    id: 1,
    image: airboothImg,
    title: "India's Pioneer Photo Booth Company Since 2012",
    subtitle: BRAND.subTagline
  },
  {
    id: 2,
    image: imirrorImg,
    title: 'Premium Photo Experiences for Every Event',
    subtitle: 'From intimate weddings to massive brand activations'
  },
  {
    id: 3,
    image: retroBoothImg,
    title: 'Create Memories That Last Forever',
    subtitle: 'Cutting-edge booths that create unforgettable moments'
  }
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Background Swiper */}
        <div className="absolute inset-0 z-0">
          <Swiper
            modules={[Autoplay, EffectFade, Pagination]}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            loop={true}
            className="h-full w-full"
          >
            {heroSlides.map((slide) => (
              <SwiperSlide key={slide.id}>
                <div className="relative h-screen w-full">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${slide.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/60" />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Hero Content */}
        <div className="relative z-20 min-h-screen flex items-center pt-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-3xl"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00BFFF]/10 border border-[#00BFFF]/20 mb-8">
                <Sparkles className="w-4 h-4 text-[#00BFFF]" />
                <span className="text-sm text-gray-700">Since 2012 - Chennai&apos;s First</span>
              </div>

              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight mb-6">
                India&apos;s Pioneer{' '}
                <span className="gold-gradient-text">Photo Booth</span>{' '}
                Company
              </h1>

              <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl leading-relaxed">
                {BRAND.subTagline}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-sm px-8 py-4">
                  Book a Photo Booth <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/gallery" className="btn-secondary text-sm px-8 py-4">
                  View Our Work
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-6 h-10 rounded-full border-2 border-gray-400 flex justify-center pt-2"
          >
            <div className="w-1.5 h-1.5 bg-gray-500 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-gray-50 border-y border-gray-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="stat-item"
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Preview */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">Our Collection</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Premium Photo Booth Experiences
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From classic booths to cutting-edge technology, we have the perfect solution for your event.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[...PRODUCTS.coreBooths.slice(0, 2), ...PRODUCTS.premiumExperiences.slice(0, 1)].map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="product-card group"
              >
                <div className="product-card-image">
                  <img src={product.image} alt={product.name} loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <span className="text-[#00BFFF] text-xs uppercase tracking-wider font-medium">
                    {product.category}
                  </span>
                  <h3 className="font-display text-xl font-semibold text-gray-900 mt-2 mb-3">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.useCases.slice(0, 2).map((useCase, i) => (
                      <span
                        key={i}
                        className={`service-tag ${useCase.includes('Corporate') ? 'corporate' :
                            useCase.includes('Wedding') ? 'wedding' : 'activation'
                          }`}
                      >
                        {useCase}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 text-[#00BFFF] text-sm font-medium group-hover:gap-3 transition-all"
                  >
                    Learn More <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products" className="btn-secondary">
              View All Products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Inkfly Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">Why Choose Us</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4">
              The Inkfly Advantage
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Award, title: "Pioneer Since 2012", desc: "Chennai's first photo booth brand, setting industry standards" },
              { icon: Zap, title: "AI-Powered Tech", desc: "Cutting-edge technology with instant social sharing" },
              { icon: Users, title: "5000+ Events", desc: "Trusted by top Corporates, Agencies, wedding planners and Event planners" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 rounded-xl bg-white border border-gray-100 hover:border-[#00BFFF]/30 hover:shadow-lg transition-all group"
              >
                <div className="w-14 h-14 rounded-xl bg-[#00BFFF]/10 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-[#00BFFF]" />
                </div>
                <h3 className="font-display text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/why-inkfly" className="btn-primary">
              Discover More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">Testimonials</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4">
              What Our Clients Say
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 rounded-xl bg-gray-50 border border-gray-100"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#00BFFF] fill-[#00BFFF]" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  &quot;{testimonial.quote}&quot;
                </p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  {testimonial.role && <p className="text-gray-500 text-sm">{testimonial.role}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Logos */}
      <section className="py-16 bg-gray-50 border-y border-gray-100">
        <div className="container-custom">
          <p className="text-center text-gray-500 text-sm uppercase tracking-widest mb-10">
            Trusted by Leading Brands
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {CLIENT_LOGOS.map((logo) => (
              <div
                key={logo.id}
                className="opacity-60 hover:opacity-100 transition-all duration-300"
              >
                <img
                  src={logo.logo}
                  alt={logo.name}
                  className="h-8 md:h-10 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="text-gray-400 font-display font-semibold text-lg">${logo.name}</span>`;
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#00BFFF]/5 via-white to-[#00BFFF]/5 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Ready to Create{' '}
              <span className="gold-gradient-text">Unforgettable Memories?</span>
            </h2>
            <p className="text-gray-600 text-lg mb-10">
              Let&apos;s discuss how we can make your next event extraordinary with our premium photo booth experiences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-sm px-10 py-4">
                Get in Touch <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/gallery" className="btn-secondary text-sm px-10 py-4">
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
